import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Sentry } from '../config/sentry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    if (!import.meta.env.PROD) {
      console.error('Error caught by boundary:', error, errorInfo);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-[var(--surface-secondary)] flex items-center justify-center p-4" style={{ '--accent-primary': '#2a9d8f', '--accent-hover': '#238578' } as React.CSSProperties}>
          <div className="bg-[var(--surface-primary)] rounded-xl shadow-lg max-w-md w-full p-6 text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-[var(--status-error-bg)] rounded-full">
                <AlertTriangle className="w-8 h-8 text-[var(--error)]" />
              </div>
            </div>

            <h1 className="text-xl font-semibold text-[var(--content-primary)] mb-2">
              Something went wrong
            </h1>

            <p className="text-[var(--content-secondary)] mb-6">
              The application encountered an unexpected error. Please try refreshing the page.
            </p>

            {!import.meta.env.PROD && this.state.error && (
              <div className="mb-6 p-3 bg-[var(--surface-tertiary)] rounded-lg text-left">
                <p className="text-xs font-mono text-[var(--content-tertiary)] break-all">
                  {this.state.error.message}
                </p>
              </div>
            )}

            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="px-4 py-2 text-sm text-[var(--content-secondary)] hover:text-[var(--content-primary)] hover:bg-[var(--surface-tertiary)] rounded-lg transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={this.handleReload}
                className="px-4 py-2 text-sm bg-[var(--accent-primary)] text-white rounded-lg hover:bg-[var(--accent-hover)] transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
